import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useDevOverride,
  type CurrentPeriod,
} from "../../context/DevOverrideContext";
import { useSchoolPasses } from "../../hooks/useSchoolPasses";
import { useClassSections, type ClassSection } from "../../hooks/useClassSections";
import { useLoadingStates } from "../../hooks/useLoadingStates";
import {
  PASS_STATUS,
  listDestinations,
  cancelPass as cancelPassApi,
  returnPass,
} from "../../api/studentPassesApi";
import {
  approvePass as approvePassApi,
  denyPass as denyPassApi,
} from "../../api/teacherPassesApi";
import {
  getScheduleDisplayName,
  ScheduleConfigurationError,
  type ABDay,
} from "../../utils/scheduleUtils";
import { showError, showSuccess, showInfo } from "../../utils/notifications";
import {
  getContextualError,
  logError,
  PASS_ALREADY_UPDATED_MESSAGE,
} from "../../utils/errorHandling";
import { periodsEqual } from "../../utils/periodFormatHelper";
import PassHistoryView from "../admin/PassHistoryView";
import TeacherPassSubmission from "./TeacherPassSubmission";
import MyClassSectionsPanel from "./MyClassSectionsPanel";
import ScheduleErrorScreen from "../common/ScheduleErrorScreen";
import LoadingScreen from "../common/LoadingScreen";
import ConnectionStatusBanner from "../common/ConnectionStatusBanner";
import SearchFilter from "../common/SearchFilter";
import { matchesPassQuery } from "../../utils/passSearch";
import type { components } from "../../api/types.gen";
import type { Pass } from "../../hooks/usePassSocket";
import styles from "./TeacherDashboard.module.css";

type DestinationResponse = components["schemas"]["DestinationResponse"];

// Minimal shape narrowed from a caught error to read its HTTP status.
interface ApiErrorLike {
  status?: number;
}

// Sort applied to the Currently Out / Over-limit buckets only.
type OutSort = "longestOut" | "newest" | "student" | "destination";

const OUT_SORT_LABELS: Record<OutSort, string> = {
  longestOut: "Longest out",
  newest: "Most recent",
  student: "Student name",
  destination: "Destination",
};

// `activatedAt` is set when a pass goes ACTIVE, so it is present on every
// pass in these buckets; the ?? 0 fallback only guards a malformed payload.
const activatedMs = (pass: Pass): number =>
  pass.activatedAt ? new Date(pass.activatedAt).getTime() : 0;

function compareOut(a: Pass, b: Pass, sort: OutSort): number {
  switch (sort) {
    case "newest":
      return activatedMs(b) - activatedMs(a);
    case "student":
      return (a.studentName ?? "").localeCompare(b.studentName ?? "");
    case "destination":
      return (a.destinationName ?? "").localeCompare(b.destinationName ?? "");
    case "longestOut":
    default:
      return activatedMs(a) - activatedMs(b);
  }
}

const TeacherDashboard = (): React.ReactElement => {
  const { currentUser, logout } = useAuth();
  // TODO(slice:foundation): the old multi-org context is gone (multi-org
  // orgs have no hallpass-backend equivalent). schoolId is the closest
  // stand-in for the utils below (scheduleUtils, AnnouncementBanner,
  // PassHistoryView, TeacherPassSubmission) that still expect an org
  // identifier and haven't been migrated off it yet.
  const schoolId = currentUser?.schoolId;
  const {
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    isAutoMode,
    isDeveloperMode,
    updateTrigger,
    scheduleVariantsMap,
  } = useDevOverride();

  const { passes, connectionStatus, refresh, getPassesForClassSection } =
    useSchoolPasses();
  // One instance for the whole dashboard: MyClassSectionsPanel mutates the
  // same list this board filters by, so it takes `refetch` rather than
  // holding a second copy that would drift out of sync after every edit.
  const {
    classSections,
    loading: classSectionsLoading,
    refetch: refetchClassSections,
  } = useClassSections();

  const [destinations, setDestinations] = useState<DestinationResponse[]>([]);
  const [destinationsError, setDestinationsError] = useState<string | null>(
    null,
  );
  const [showPassSubmission, setShowPassSubmission] = useState(false);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState("");
  const [hasUserSelectedSection, setHasUserSelectedSection] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [hasUserSelectedPeriod, setHasUserSelectedPeriod] = useState(false);
  // Opt-in strict filter: show ONLY passes belonging to the selected class
  // section, excluding the unresolved-section passes the default view keeps.
  // Off by default so the board stays fail-open (see the filter below).
  const [onlyMyRoster, setOnlyMyRoster] = useState(false);
  const [currentPeriod, setCurrentPeriod] = useState<CurrentPeriod | null>(
    null,
  );
  const [scheduleType, setScheduleType] = useState<string | null>(null);
  const [abDay, setABDay] = useState<ABDay | string | null>("A");

  // Tick state for updating over-limit calculations. Read only as a memo
  // dependency below: the over-limit split is time-dependent, so it has to
  // recompute when the 30s interval fires rather than staying frozen at
  // whatever the last input-driven render produced.
  const [tick, setTick] = useState(0);

  // Free-text board search and the sort applied to the "currently out"
  // buckets. Pending/Waiting stay FIFO — see the buckets below.
  const [searchQuery, setSearchQuery] = useState("");
  const [outSort, setOutSort] = useState<OutSort>("longestOut");

  // Schedule error state
  const [scheduleError, setScheduleError] = useState<Error | null>(null);

  // Initial loading state
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Loading states for async operations
  const [loadingStates, setLoading] = useLoadingStates({
    denyPass: false,
    markReturned: false,
    cancelPass: false,
  });

  // Approve is tracked per-pass (by id) rather than as a single shared
  // boolean: multiple pending cards render their own Approve button, and a
  // shared flag would disable/relabel every card while any one approval is
  // in flight. Multiple approvals can be in flight at once, each tracked
  // independently by its pass id in this Set, so one approval completing
  // does not clear another's "Approving..." indicator (empty when idle).
  const [approvingPassIds, setApprovingPassIds] = useState<Set<number>>(
    () => new Set(),
  );

  // Distinct periodName values present in this school's class sections,
  // used to populate the period dropdown (mirrors ActivePassesPanel.js's
  // periodNameByClassSectionId pattern, which resolves periods from the
  // same field).
  const periodOptions = useMemo(() => {
    const names = new Set<string>();
    classSections.forEach((cs) => {
      if (cs.periodName) names.add(cs.periodName);
    });
    return Array.from(names).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [classSections]);

  // Resolve a display name for a pass's classSectionId from the
  // useClassSections data (replaces the old per-pass classroom-name field).
  const classSectionById = useMemo(() => {
    const map = new Map<number, ClassSection>();
    classSections.forEach((cs) => map.set(cs.id, cs));
    return map;
  }, [classSections]);

  const getClassSectionName = (classSectionId: number | null): string => {
    if (classSectionId == null) return "Unassigned";
    const section = classSectionById.get(classSectionId);
    return section ? `${section.periodName} - ${section.name}` : "Unknown";
  };

  // Helper function to check if a pass has been out over 10 minutes
  const isOverLimit = (pass: Pass): boolean => {
    if (!pass.activatedAt) return false;
    const activatedTime = new Date(pass.activatedAt);
    const now = new Date();
    const minutesOut = (now.getTime() - activatedTime.getTime()) / (1000 * 60);
    return minutesOut > 10;
  };

  // Update current period from DevOverrideContext's backend-driven state.
  useEffect(() => {
    const updateCurrentInfo = () => {
      try {
        const schedule = getScheduleType();
        const period = getCurrentPeriod();
        const currentABDay = getABDay();

        setScheduleType(schedule);
        setCurrentPeriod(period);
        setABDay(currentABDay);
        setScheduleError(null); // Clear any previous errors

        // Set intelligent default for the period filter, resolving the
        // backend-derived period number/name against the school's actual
        // (possibly custom) periodName values.
        if (
          !hasUserSelectedPeriod &&
          period &&
          period.periodNumber !== "Advisory" &&
          period.periodNumber !== "Lunch"
        ) {
          const matchedSection = classSections.find((cs) =>
            periodsEqual(period.periodNumber, cs.periodName),
          );
          if (matchedSection) {
            setSelectedPeriod(matchedSection.periodName);
          }
        }
      } catch (error) {
        if (error instanceof ScheduleConfigurationError) {
          console.error(
            "TeacherDashboard: Schedule configuration error:",
            error.message,
          );
          setScheduleError(error);
          setCurrentPeriod(null);
          setScheduleType(null);
        } else {
          console.error(
            "TeacherDashboard: Unexpected error in period update:",
            error,
          );
          // Don't show schedule error for other types of errors
        }
      }
    };

    // Initial update only - backend period detection handles the rest
    updateCurrentInfo();
    // No polling interval - DevOverrideContext listens to backend period state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    updateTrigger,
    classSections,
    hasUserSelectedPeriod,
  ]);

  // Fetch destinations once on mount, for the "Create Pass for Student"
  // modal's destination picker. Unlike the old real-time listener, this is
  // a one-time fetch - an admin-side change won't show up here until reload.
  //
  // Guards against a stale response clobbering `destinations` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the cleanup
  // below flips `cancelled` so an earlier request that resolves after a
  // later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in AdminPanel.js's checkSchoolSetupStatus effect.
  //
  // Surfaces a fetch failure (visible error + Retry, mirroring
  // StudentDashboard's fetchDestinations) instead of only logging it: an
  // unhandled failure here left the Create-Pass modal's destination dropdown
  // empty and its submit permanently disabled, with no indication why.
  const fetchDestinations = useCallback(
    (isCancelled: () => boolean = () => false) => {
      if (!schoolId) return;
      if (!isCancelled()) setDestinationsError(null);
      listDestinations(schoolId)
        .then(({ data }) => {
          if (!isCancelled()) {
            setDestinations(
              [...data].sort((a, b) => a.name.localeCompare(b.name)),
            );
          }
        })
        .catch((error) => {
          logError(error, "fetchDestinations");
          if (!isCancelled()) {
            setDestinationsError(getContextualError(error, "teacher"));
          }
        });
    },
    [schoolId],
  );

  useEffect(() => {
    let cancelled = false;
    fetchDestinations(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchDestinations]);

  // Set initial loading to false once we have basic data
  useEffect(() => {
    if (currentUser && schoolId) {
      // Give a brief moment for UI to initialize
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentUser, schoolId]);

  // Pre-select the current teacher's own class section(s) on load (replaces
  // the old last-name-vs-destination-subcategory matching, which has no
  // backend equivalent). Falls back to the unfiltered "All" view if no
  // section belongs to this teacher.
  useEffect(() => {
    if (
      hasUserSelectedSection ||
      selectedClassSectionId !== "" ||
      classSectionsLoading
    ) {
      return;
    }

    const ownSection = classSections.find(
      (cs) => cs.teacherId === currentUser?.id,
    );
    if (ownSection) {
      setSelectedClassSectionId(String(ownSection.id));
    }
  }, [
    classSections,
    classSectionsLoading,
    hasUserSelectedSection,
    selectedClassSectionId,
    currentUser,
  ]);

  // Update tick every 30 seconds to recalculate over-limit passes
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const approvePass = async (passId: number) => {
    setApprovingPassIds((prev) => {
      const next = new Set(prev);
      next.add(passId);
      return next;
    });
    try {
      await approvePassApi(passId);
      showSuccess("Pass approved successfully");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "approvePass");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setApprovingPassIds((prev) => {
        const next = new Set(prev);
        next.delete(passId);
        return next;
      });
    }
  };

  const denyPass = async (passId: number) => {
    setLoading("denyPass", true);
    try {
      await denyPassApi(passId);
      showSuccess("Pass denied");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "denyPass");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setLoading("denyPass", false);
    }
  };

  const cancelPass = async (passId: number) => {
    if (!window.confirm("Cancel this pass? This action cannot be undone.")) {
      return;
    }

    setLoading("cancelPass", true);
    try {
      await cancelPassApi(passId);
      showSuccess("Pass canceled successfully");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "cancelPass");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setLoading("cancelPass", false);
    }
  };

  const markReturned = async (passId: number) => {
    setLoading("markReturned", true);
    try {
      await returnPass(passId);
      showSuccess("Student marked as returned");
      await refresh();
    } catch (error) {
      if ((error as ApiErrorLike).status === 409) {
        showInfo(PASS_ALREADY_UPDATED_MESSAGE);
        await refresh();
      } else {
        logError(error, "markReturn");
        showError(getContextualError(error, "teacher"));
      }
    } finally {
      setLoading("markReturned", false);
    }
  };

  // Filter the live board by the selected class section. `onlyMyRoster`
  // swaps getPassesForClassSection's deliberately fail-open behavior (it also
  // returns passes whose section can't be resolved) for a strict
  // "this section only" match.
  const classSectionFilteredPasses = useMemo(
    () =>
      selectedClassSectionId
        ? onlyMyRoster
          ? passes.filter(
              (pass) => pass.classSectionId === Number(selectedClassSectionId),
            )
          : getPassesForClassSection(Number(selectedClassSectionId))
        : passes,
    [passes, selectedClassSectionId, onlyMyRoster, getPassesForClassSection],
  );

  // Further filter by the selected period, matching against the pass's
  // class section periodName as-is (selectedPeriod is itself a periodName
  // value sourced from classSections, so plain equality is sufficient).
  //
  // A pass whose section can't be RESOLVED is kept rather than dropped — it
  // has no period to disagree with, so filtering it out hides it entirely.
  // Two cases reach this and both are normal:
  //   - classSectionId === null: the student has no enrollment for this
  //     period (POST /passes fails open rather than refusing the request).
  //   - classSectionId set but absent from classSectionById: the section was
  //     soft-deleted; useClassSections returns only live ones. The display
  //     path already renders this as "Unknown" (getClassSectionName above).
  // Dropping them here silently undid the null-inclusive intent of
  // useSchoolPasses' getPassesForClassSection one filter earlier, so at a
  // partially-rostered school an unenrolled student's pass was invisible
  // once the period filter auto-selected. Use "Only my roster" to exclude
  // them deliberately.
  const periodFilteredPasses = useMemo(
    () =>
      selectedPeriod
        ? classSectionFilteredPasses.filter((pass) => {
            const section = pass.classSectionId
              ? classSectionById.get(pass.classSectionId)
              : undefined;
            return !section || section.periodName === selectedPeriod;
          })
        : classSectionFilteredPasses,
    [classSectionFilteredPasses, selectedPeriod, classSectionById],
  );

  // Search is applied last, so it narrows within whatever section/period
  // view is active rather than escaping it.
  const filteredPasses = useMemo(
    () => periodFilteredPasses.filter((pass) => matchesPassQuery(pass, searchQuery)),
    [periodFilteredPasses, searchQuery],
  );

  // Pending and Waiting stay FIFO regardless of the sort control:
  // first-come-first-served is the queue's meaning, not a display preference.
  const pendingPasses = useMemo(
    () =>
      filteredPasses
        .filter((p) => p.status === PASS_STATUS.PENDING)
        .sort(
          (a, b) =>
            new Date(a.requestedAt).getTime() -
            new Date(b.requestedAt).getTime(),
        ),
    [filteredPasses],
  );

  // Sorted once here, then split — the split preserves order, so both
  // "Currently Out" and "Over 10 Minute Limit" honor the same sort.
  const currentlyOutPasses = useMemo(
    () =>
      filteredPasses
        .filter((p) => p.status === PASS_STATUS.ACTIVE)
        .sort((a, b) => compareOut(a, b, outSort)),
    [filteredPasses, outSort],
  );

  // `tick` is a real dependency: isOverLimit compares against Date.now(), so
  // this split must be recomputed on the 30s interval, not just when the
  // inputs change.
  const overLimitPasses = useMemo(
    () => currentlyOutPasses.filter((p) => isOverLimit(p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentlyOutPasses, tick],
  );
  const outPassesInTime = useMemo(
    () => currentlyOutPasses.filter((p) => !isOverLimit(p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentlyOutPasses, tick],
  );

  const waitingPasses = useMemo(
    () =>
      filteredPasses
        .filter((p) => p.status === PASS_STATUS.WAITING)
        .sort(
          (a, b) =>
            new Date(a.approvedAt!).getTime() -
            new Date(b.approvedAt!).getTime(),
        ), // Sort by approval time (FIFO)
    [filteredPasses],
  );

  // Show initial loading screen
  if (isInitialLoading) {
    return <LoadingScreen message="Loading teacher dashboard..." />;
  }

  // Show schedule error screen if there's a configuration issue
  if (scheduleError) {
    return <ScheduleErrorScreen error={scheduleError} />;
  }

  return (
    <div className={styles["dashboard"]}>
      <header>
        <div>
          <h1>
            Teacher Dashboard
            {isDeveloperMode && (
              <span className={styles["dev-mode-badge"]}>🔧 DEV MODE</span>
            )}
          </h1>
          {currentPeriod ? (
            <div className={styles["period-info"]}>
              <span className={styles["current-period"]}>
                {currentPeriod.displayName}
              </span>
              {/* GET /schedule/today carries no abDay, so this is null today;
                  omit the "(_ Day)" suffix entirely rather than rendering a
                  stray "( Day)". */}
              {currentPeriod.abDay && (
                <span className={styles["ab-day"]}>
                  ({currentPeriod.abDay} Day)
                </span>
              )}
              <span className={styles["schedule-type"]}>
                •{" "}
                {scheduleVariantsMap[scheduleType as string] ||
                  getScheduleDisplayName(scheduleType)}
                {/* TODO(slice:foundation): see schoolId comment above */}
              </span>
              {isAutoMode && (
                <span className={styles["time-remaining"]}>
                  • {currentPeriod.timeRemaining} min left
                </span>
              )}
            </div>
          ) : (
            <div className={styles["period-info"]}>
              <span className={styles["no-period"]}>No active period</span>
              {abDay && <span className={styles["ab-day"]}>({abDay} Day)</span>}
              {scheduleType && (
                <span className={styles["schedule-type"]}>
                  •{" "}
                  {scheduleVariantsMap[scheduleType] ||
                    getScheduleDisplayName(scheduleType)}
                  {/* TODO(slice:foundation): see schoolId comment above */}
                </span>
              )}
            </div>
          )}
        </div>
        <div>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLScdXhb7Qs0IOmSG3UoMLMqOP1CBpoSKFbdAe6iExkyrenIGqg/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className={styles["bug-report-link"]}
          >
            🐛 Report Bug
          </a>
          <span>Welcome, {currentUser?.name}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </header>

      <ConnectionStatusBanner connectionStatus={connectionStatus} />

      <div className={styles["teacher-controls"]}>
        <div
          className={styles["action-buttons"]}
          style={{ display: "flex", gap: "10px", marginBottom: "15px" }}
        >
          <button
            className={styles["create-pass-btn"]}
            onClick={() => setShowPassSubmission(true)}
          >
            ➕ Create Pass for Student
          </button>
        </div>

        <div className={styles["control-group"]}>
          {/* 150ms rather than the 500ms default: this filters an in-memory
              list, so the longer delay is just dead typing. */}
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            debounceMs={150}
            placeholder="Search student or destination..."
            ariaLabel="Search passes by student or destination"
          />
        </div>

        <div className={styles["control-group"]}>
          <label htmlFor="class-section-select">Class Section:</label>
          <select
            id="class-section-select"
            value={selectedClassSectionId}
            onChange={(e) => {
              setSelectedClassSectionId(e.target.value);
              setHasUserSelectedSection(true);
            }}
            className={styles["class-section-select"]}
          >
            <option value="">All Class Sections</option>
            {classSections.map((cs) => (
              <option key={cs.id} value={cs.id}>
                {cs.periodName} - {cs.name}
              </option>
            ))}
          </select>
        </div>

        {/* Only meaningful once a specific section is selected — with "All
            Class Sections" there is nothing to narrow to. */}
        {selectedClassSectionId && (
          <div className={styles["control-group"]}>
            <label htmlFor="only-my-roster">
              <input
                type="checkbox"
                id="only-my-roster"
                checked={onlyMyRoster}
                onChange={(e) => setOnlyMyRoster(e.target.checked)}
              />{" "}
              Only my roster
            </label>
            <span className={styles["viewing-info"]}>
              {onlyMyRoster
                ? "Hiding students who aren't enrolled in this section."
                : "Also showing students with no class assigned this period."}
            </span>
          </div>
        )}

        <div className={styles["control-group"]}>
          <label htmlFor="period-select">Period:</label>
          <select
            id="period-select"
            value={selectedPeriod}
            onChange={(e) => {
              setSelectedPeriod(e.target.value);
              setHasUserSelectedPeriod(true);
            }}
            className={styles["period-select"]}
          >
            <option value="">All periods</option>
            {periodOptions.map((periodName) => (
              <option key={periodName} value={periodName}>
                {periodName}
              </option>
            ))}
          </select>
        </div>

        <div className={styles["control-group"]}>
          <span className={styles["viewing-info"]}>
            Viewing:{" "}
            {selectedClassSectionId
              ? getClassSectionName(Number(selectedClassSectionId))
              : "All Class Sections"}{" "}
            - {selectedPeriod || "All Periods"}
          </span>
        </div>
      </div>

      <main>
        <div className={styles["pass-sections"]}>
          <section className="pending-section">
            <h2>📋 Pending Requests ({pendingPasses.length})</h2>
            {pendingPasses.length === 0 ? (
              <p>No pending requests.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {pendingPasses.map((pass) => (
                  <div key={pass.id} className={styles["pass-card"]}>
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <small>
                        Requested:{" "}
                        {new Date(pass.requestedAt).toLocaleTimeString()}
                      </small>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => approvePass(pass.id)}
                        disabled={approvingPassIds.has(pass.id)}
                        className={styles["approve-btn"]}
                      >
                        {approvingPassIds.has(pass.id)
                          ? "Approving..."
                          : "Approve"}
                      </button>
                      <button
                        onClick={() => denyPass(pass.id)}
                        disabled={loadingStates.denyPass}
                        className={styles["deny-btn"]}
                      >
                        {loadingStates.denyPass ? "Denying..." : "Deny"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="approved-section">
            <h2>✅ Approved & Waiting ({waitingPasses.length})</h2>
            {waitingPasses.length === 0 ? (
              <p>No students waiting to leave.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {waitingPasses.map((pass) => (
                  <div
                    key={pass.id}
                    className={`${styles["pass-card"]} ${styles["waiting-pass"]}`}
                  >
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <small>
                        Approved:{" "}
                        {new Date(pass.approvedAt!).toLocaleTimeString()}
                      </small>
                      <span className={styles["waiting-badge"]}>
                        Destination Full
                      </span>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => cancelPass(pass.id)}
                        disabled={loadingStates.cancelPass}
                        className={styles["cancel-btn"]}
                      >
                        {loadingStates.cancelPass ? "Canceling..." : "Cancel"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="active-section">
            <h2>🚶 Currently Out ({outPassesInTime.length})</h2>
            {/* Scoped to this section (and the over-limit one below it) on
                purpose: Pending and Waiting are FIFO queues, so a board-wide
                sort control would appear to do nothing to half the board. */}
            <div className={styles["control-group"]}>
              <label htmlFor="out-sort-select">Sort by:</label>
              <select
                id="out-sort-select"
                value={outSort}
                onChange={(e) => setOutSort(e.target.value as OutSort)}
              >
                {(Object.keys(OUT_SORT_LABELS) as OutSort[]).map((key) => (
                  <option key={key} value={key}>
                    {OUT_SORT_LABELS[key]}
                  </option>
                ))}
              </select>
            </div>
            {outPassesInTime.length === 0 ? (
              <p>
                {searchQuery.trim()
                  ? `No students currently out match "${searchQuery.trim()}".`
                  : "No students currently out from your class."}
              </p>
            ) : (
              <div className={styles["pass-list"]}>
                {outPassesInTime.map((pass) => (
                  <div key={pass.id} className={styles["pass-card"]}>
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <small>
                        Left: {new Date(pass.activatedAt!).toLocaleTimeString()}
                      </small>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => markReturned(pass.id)}
                        disabled={loadingStates.markReturned}
                        className={styles["return-btn"]}
                      >
                        {loadingStates.markReturned
                          ? "Marking..."
                          : "Mark Returned"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Over Limit Section - Students out longer than 10 minutes */}
          <section className="over-limit-section">
            <h2 style={{ color: "#856404" }}>
              ⚠️ Over 10 Minute Limit ({overLimitPasses.length})
            </h2>
            {overLimitPasses.length === 0 ? (
              <p>No students currently over the time limit.</p>
            ) : (
              <div className={styles["pass-list"]}>
                {overLimitPasses.map((pass) => (
                  <div
                    key={pass.id}
                    className={styles["pass-card"]}
                    style={{
                      backgroundColor: "#fff3cd",
                      borderColor: "#ffc107",
                    }}
                  >
                    <div className={styles["pass-info"]}>
                      <strong>{pass.studentName}</strong>
                      <p>{pass.destinationName}</p>
                      <p>
                        <small>
                          from {getClassSectionName(pass.classSectionId)}
                        </small>
                      </p>
                      <div
                        style={{
                          backgroundColor: "#ffc107",
                          color: "#856404",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                          fontWeight: "bold",
                          display: "inline-block",
                          marginTop: "5px",
                        }}
                      >
                        Over 10 min
                      </div>
                      <small style={{ display: "block", marginTop: "5px" }}>
                        Left: {new Date(pass.activatedAt!).toLocaleTimeString()}
                      </small>
                    </div>
                    <div className={styles["pass-actions"]}>
                      <button
                        onClick={() => markReturned(pass.id)}
                        disabled={loadingStates.markReturned}
                        className={styles["return-btn"]}
                      >
                        {loadingStates.markReturned
                          ? "Marking..."
                          : "Mark Returned"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* TODO: En Route section removed — no destination-to-classSection/teacher mapping exists in hallpass-backend; revisit if the backend adds one */}

        {/* Optional: the teacher's own class sections and rosters. Placed
            after the live board because it's setup, not the daily task. */}
        <MyClassSectionsPanel
          classSections={classSections}
          refetchClassSections={refetchClassSections}
          loading={classSectionsLoading}
        />

        {/* Pass History Analytics Section */}
        <section className={styles["pass-history-section"]}>
          <h2>📊 Pass History & Analytics</h2>
          {/* TODO(slice:foundation): see schoolId comment above */}
          <PassHistoryView orgId={schoolId} />
        </section>
      </main>

      {/* Pass Submission Modal */}
      <TeacherPassSubmission
        isOpen={showPassSubmission}
        onClose={() => setShowPassSubmission(false)}
        destinations={destinations}
        destinationsError={destinationsError}
        onRetryDestinations={fetchDestinations}
      />
    </div>
  );
};

export default TeacherDashboard;
